import { useState, useMemo, forwardRef, useImperativeHandle, useCallback, useEffect } from 'react';
import {
  GridSortModel,
  getGridStringOperators,
  GridFilterModel,
  GridColumns,
  GridSelectionModel,
  DataGridPro,
} from '@mui/x-data-grid-pro';
import { PaginationAndSortingParams } from 'types/api/Common/PaginationTypes';
import { useGetStandardViewCubeAttributesDropdown } from 'services/v1/SystemTenant/AROView/StandardViewDefinitionService';

export const limitAttributeDataGridStyle = {
  '& .MuiCheckbox-root svg': {
    backgroundColor: 'transparent',
    color: '#98A2AE',
    borderRadius: 2,
  },
  '& .MuiDataGrid-columnHeaders': {
    backgroundColor: '#EDF2FF',
    borderTop: '1px solid #E3EBF6',
    borderBottom: '1px solid #E3EBF6',
    textAlign: 'center',
    display: 'flex',
  },
};
interface IProps {
  initialData?: GridSelectionModel;
  cubeId?: string;
  ref: React.RefObject<IInsightViewLimitAttributeDefinitionRef>;
}

export interface IInsightViewLimitAttributeDefinitionRef {
  getRowData: () => string[];
  setRowData: (data: GridSelectionModel) => void;
}

export const InsightViewLimitAttributeDefinition = forwardRef<IInsightViewLimitAttributeDefinitionRef, IProps>(
  (props, ref) => {
    const { initialData, cubeId } = props;
    const { data: cubeAttributeDropdown } = useGetStandardViewCubeAttributesDropdown(cubeId);
    const [selectedDataID, setSelectedDataID] = useState<GridSelectionModel>([]);
    const [filter, setFilter] = useState<PaginationAndSortingParams>({
      page: 0,
      size: 100,
      filterValue: '',
      sortType: '',
      filterOperator: '',
    });

    const columnsAttribute = useMemo<GridColumns>(() => {
      return [
        {
          field: 'label',
          headerName: 'Cube Attribute',
          width: 240,
          editable: false,
          filterable: true,
          sortable: true,
          filterOperators: getGridStringOperators().filter((operator) =>
            ['contains', 'equals'].includes(operator.value)
          ),
        },
      ];
    }, []);

    const handleOnSortModelChange = (model: GridSortModel) => {
      if (model.length === 0) {
        setFilter({ ...filter, sortType: 'asc' });
        return;
      }
      setFilter({ ...filter, sortType: model[0].sort });
    };

    const handleOnFilterModelChange = (params: GridFilterModel) => {
      let filterObj = {};
      if (params.items.length) {
        filterObj = {
          filterValue: params.items[0].value,
          filterOperator: params.items[0].operatorValue,
        };
      } else {
        filterObj = {
          filterValue: '',
          filterOperator: 'contains',
        };
      }
      setFilter({
        ...filter,
        ...filterObj,
      });
    };

    const handleChangePage = (newPage: number) => {
      setFilter({ ...filter, page: newPage });
    };

    const handleChangePageSize = (newPageSize: number) => {
      setFilter({ ...filter, size: newPageSize });
    };

    useImperativeHandle(ref, () => ({
      getRowData: () => selectedDataID.map((item) => item.toString()),
      setRowData: (data: GridSelectionModel) => {
        setSelectedDataID(data);
      },
    }));

    const getRowId = useCallback((row) => {
      return row.value;
    }, []);

    useEffect(() => {
      if (initialData && cubeAttributeDropdown?.data) {
        setSelectedDataID(initialData);
      }
    }, [initialData, cubeAttributeDropdown?.data]);

    return (
      <>
        <DataGridPro
          rows={cubeAttributeDropdown?.data ?? []}
          checkboxSelection
          onSelectionModelChange={(selectedId: GridSelectionModel) => {
            setSelectedDataID(selectedId);
          }}
          columns={columnsAttribute}
          selectionModel={selectedDataID}
          getRowId={getRowId}
          sortingMode='client'
          filterMode='client'
          paginationMode='client'
          onSortModelChange={handleOnSortModelChange}
          onFilterModelChange={handleOnFilterModelChange}
          onPageChange={handleChangePage}
          onPageSizeChange={handleChangePageSize}
          rowHeight={45}
          autoHeight
          sx={{ ...limitAttributeDataGridStyle, height: '300px' }}
        />
      </>
    );
  }
);
